<script lang="ts">
  import { onMount } from 'svelte';
  import { unitPreference, formatVolume, formatFlowRate, formatPressure, formatTemperature, type UnitPreference } from './lib/stores/preferences';
  import { 
    Sliders, Gauge, Sun, Moon, Monitor, Bell, Volume2, 
    RefreshCw, Database, CheckCircle2, ShieldCheck, 
    Droplets, Thermometer, ArrowRight, Sparkles, Check
  } from 'lucide-svelte';

  let currentTheme = $state('auto');
  let audioAlerts = $state(localStorage.getItem('saya_audio_alerts') === 'true');
  let telemetryInterval = $state(localStorage.getItem('saya_telemetry_interval') || '15');
  let cacheCleared = $state(false);

  onMount(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light' || savedTheme === 'dark') {
      currentTheme = savedTheme;
    } else {
      currentTheme = 'auto';
    }
  });

  function setTheme(theme: string) {
    currentTheme = theme;
    if (theme === 'auto') {
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('theme');
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } else {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }

  function toggleAudioAlerts() {
    audioAlerts = !audioAlerts;
    localStorage.setItem('saya_audio_alerts', String(audioAlerts));
  }

  function handleIntervalChange(val: string) {
    telemetryInterval = val;
    localStorage.setItem('saya_telemetry_interval', val);
  }

  function clearClientCache() {
    const unit = $unitPreference;
    sessionStorage.clear();
    localStorage.removeItem('saya_audio_alerts');
    localStorage.removeItem('saya_telemetry_interval');
    $unitPreference = unit; // preserve unit
    cacheCleared = true;
    setTimeout(() => { cacheCleared = false; }, 3000);
  }
</script>

<div class="space-y-6 max-w-5xl mx-auto pb-12">
  <!-- Executive Header -->
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-base-content/10">
    <div>
      <div class="flex items-center gap-2 mb-1">
        <div class="p-2 rounded-xl bg-primary/10 text-primary">
          <Sliders class="w-5 h-5" />
        </div>
        <h1 class="text-2xl md:text-3xl font-extrabold tracking-tight text-base-content">
          Preferences
        </h1>
        <span class="badge badge-sm badge-neutral font-bold uppercase tracking-wider text-[10px]">Client Console</span>
      </div>
      <p class="text-xs md:text-sm text-base-content/60">
        Configure telemetry formatting, application appearance, audible notifications, and local client behavior.
      </p>
    </div>

    <div class="flex items-center gap-2">
      <span class="text-xs font-semibold text-base-content/60">Persisted locally:</span>
      <span class="badge badge-success badge-sm font-bold gap-1 text-[11px]">
        <CheckCircle2 class="w-3 h-3" /> Auto-saved
      </span>
    </div>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
    <!-- Measurement Units Section -->
    <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5 md:p-6 space-y-4">
      <div class="flex items-center justify-between">
        <h2 class="text-sm font-black uppercase tracking-wider text-base-content/70 flex items-center gap-2">
          <Gauge class="w-4 h-4 text-primary" /> Units & Telemetry Scaling
        </h2>
        <span class="badge badge-primary badge-sm font-bold uppercase tracking-wider text-[10px]">
          {$unitPreference}
        </span>
      </div>

      <p class="text-xs text-base-content/60">
        Choose your preferred system of measurement. All volume charts, flow rate telemetry, pressure gauges, and temperature sensors dynamically convert in real time.
      </p>

      <!-- Accessible Label and Select (ensuring test contract compliance) -->
      <div>
        <label for="units" class="block text-xs font-bold text-base-content/80 mb-1.5">
          Measurement units
        </label>
        <select 
          id="units" 
          class="select select-bordered select-sm w-full rounded-xl text-xs font-semibold"
          bind:value={$unitPreference}
        >
          <option value="metric">Metric (SI Standard: Litres, L/h, kPa, °C)</option>
          <option value="imperial">Imperial (US Standard: Gallons, GPM, PSI, °F)</option>
        </select>
      </div>

      <!-- Interactive Unit Cards -->
      <div class="grid grid-cols-2 gap-3 pt-1">
        <button 
          type="button"
          onclick={() => $unitPreference = 'metric'}
          class="p-3.5 rounded-xl border text-left transition-all relative {$unitPreference === 'metric' ? 'border-primary bg-primary/5 shadow-xs' : 'border-base-content/10 hover:border-base-content/20'}"
        >
          {#if $unitPreference === 'metric'}
            <div class="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-primary text-primary-content flex items-center justify-center">
              <Check class="w-2.5 h-2.5" />
            </div>
          {/if}
          <div class="font-extrabold text-xs text-base-content mb-1">Metric (SI)</div>
          <div class="space-y-0.5 text-[11px] text-base-content/60 font-mono">
            <div>Volume: <strong class="text-base-content">Litres (L)</strong></div>
            <div>Flow: <strong class="text-base-content">L/h</strong></div>
            <div>Pressure: <strong class="text-base-content">kPa</strong></div>
            <div>Temp: <strong class="text-base-content">°C</strong></div>
          </div>
        </button>

        <button 
          type="button"
          onclick={() => $unitPreference = 'imperial'}
          class="p-3.5 rounded-xl border text-left transition-all relative {$unitPreference === 'imperial' ? 'border-primary bg-primary/5 shadow-xs' : 'border-base-content/10 hover:border-base-content/20'}"
        >
          {#if $unitPreference === 'imperial'}
            <div class="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-primary text-primary-content flex items-center justify-center">
              <Check class="w-2.5 h-2.5" />
            </div>
          {/if}
          <div class="font-extrabold text-xs text-base-content mb-1">Imperial (US)</div>
          <div class="space-y-0.5 text-[11px] text-base-content/60 font-mono">
            <div>Volume: <strong class="text-base-content">Gallons (gal)</strong></div>
            <div>Flow: <strong class="text-base-content">GPM</strong></div>
            <div>Pressure: <strong class="text-base-content">PSI</strong></div>
            <div>Temp: <strong class="text-base-content">°F</strong></div>
          </div>
        </button>
      </div>

      <!-- Live Telemetry Scaling Preview Box -->
      <div class="p-3.5 rounded-xl bg-base-content/[0.02] border border-base-content/5 space-y-2">
        <div class="text-[11px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1.5">
          <Sparkles class="w-3 h-3 text-warning" /> Live Telemetry Preview
        </div>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="p-2 rounded-lg bg-base-100 border border-base-content/5">
            <span class="text-[10px] text-base-content/50 block">Sample Volume</span>
            <span class="font-mono font-bold text-primary">{formatVolume(3785.41, $unitPreference)}</span>
          </div>
          <div class="p-2 rounded-lg bg-base-100 border border-base-content/5">
            <span class="text-[10px] text-base-content/50 block">Sample Flow Rate</span>
            <span class="font-mono font-bold text-secondary">{formatFlowRate(600, $unitPreference)}</span>
          </div>
          <div class="p-2 rounded-lg bg-base-100 border border-base-content/5">
            <span class="text-[10px] text-base-content/50 block">Sample Pressure</span>
            <span class="font-mono font-bold text-base-content">{formatPressure(350, $unitPreference)}</span>
          </div>
          <div class="p-2 rounded-lg bg-base-100 border border-base-content/5">
            <span class="text-[10px] text-base-content/50 block">Water Temp</span>
            <span class="font-mono font-bold text-base-content">{formatTemperature(21.5, $unitPreference)}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Appearance & Theme Section -->
    <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5 md:p-6 space-y-4">
      <h2 class="text-sm font-black uppercase tracking-wider text-base-content/70 flex items-center gap-2">
        <Sun class="w-4 h-4 text-warning" /> Appearance & Theme
      </h2>
      <p class="text-xs text-base-content/60">
        Choose your interface theme. Auto mode aligns automatically with your operating system color scheme.
      </p>

      <div class="grid grid-cols-3 gap-3 pt-1">
        <!-- Auto / System Theme Card -->
        <button 
          type="button"
          onclick={() => setTheme('auto')}
          class="p-3.5 rounded-xl border text-center transition-all flex flex-col items-center gap-2 relative {currentTheme === 'auto' ? 'border-primary bg-primary/5 shadow-xs font-bold' : 'border-base-content/10 hover:border-base-content/20'}"
        >
          <div class="w-8 h-8 rounded-full bg-base-content/10 flex items-center justify-center text-base-content">
            <Monitor class="w-4 h-4" />
          </div>
          <div>
            <div class="text-xs text-base-content">System</div>
            <div class="text-[10px] text-base-content/50 font-normal">Auto-detect</div>
          </div>
          {#if currentTheme === 'auto'}
            <div class="w-1.5 h-1.5 rounded-full bg-primary mt-1"></div>
          {/if}
        </button>

        <!-- Light Theme Card -->
        <button 
          type="button"
          onclick={() => setTheme('light')}
          class="p-3.5 rounded-xl border text-center transition-all flex flex-col items-center gap-2 relative {currentTheme === 'light' ? 'border-primary bg-primary/5 shadow-xs font-bold' : 'border-base-content/10 hover:border-base-content/20'}"
        >
          <div class="w-8 h-8 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Sun class="w-4 h-4" />
          </div>
          <div>
            <div class="text-xs text-base-content">Light</div>
            <div class="text-[10px] text-base-content/50 font-normal">High contrast</div>
          </div>
          {#if currentTheme === 'light'}
            <div class="w-1.5 h-1.5 rounded-full bg-primary mt-1"></div>
          {/if}
        </button>

        <!-- Dark Theme Card -->
        <button 
          type="button"
          onclick={() => setTheme('dark')}
          class="p-3.5 rounded-xl border text-center transition-all flex flex-col items-center gap-2 relative {currentTheme === 'dark' ? 'border-primary bg-primary/5 shadow-xs font-bold' : 'border-base-content/10 hover:border-base-content/20'}"
        >
          <div class="w-8 h-8 rounded-full bg-indigo-500/10 text-indigo-400 flex items-center justify-center">
            <Moon class="w-4 h-4" />
          </div>
          <div>
            <div class="text-xs text-base-content">Dark</div>
            <div class="text-[10px] text-base-content/50 font-normal">Midnight slate</div>
          </div>
          {#if currentTheme === 'dark'}
            <div class="w-1.5 h-1.5 rounded-full bg-primary mt-1"></div>
          {/if}
        </button>
      </div>

      <!-- Telemetry Cadence & Notifications -->
      <div class="pt-4 border-t border-base-content/10 space-y-3.5">
        <h3 class="text-xs font-bold text-base-content uppercase tracking-wider flex items-center gap-2">
          <Bell class="w-3.5 h-3.5 text-secondary" /> Telemetry & Notification Channels
        </h3>

        <!-- Audio Alert Toggle -->
        <div class="flex items-center justify-between p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5">
          <div class="flex items-center gap-2.5">
            <div class="p-1.5 rounded-lg bg-base-content/5 text-base-content/70">
              <Volume2 class="w-4 h-4" />
            </div>
            <div>
              <p class="text-xs font-bold text-base-content">Audible Alarm Tones</p>
              <p class="text-[11px] text-base-content/60">Play sound effect on critical pipe burst or emergency shutoff</p>
            </div>
          </div>
          <input 
            type="checkbox" 
            class="toggle toggle-sm toggle-primary" 
            checked={audioAlerts}
            onchange={toggleAudioAlerts}
          />
        </div>

        <!-- Polling Cadence Selector -->
        <div class="flex items-center justify-between p-3 rounded-xl bg-base-content/[0.02] border border-base-content/5">
          <div class="flex items-center gap-2.5">
            <div class="p-1.5 rounded-lg bg-base-content/5 text-base-content/70">
              <RefreshCw class="w-4 h-4" />
            </div>
            <div>
              <p class="text-xs font-bold text-base-content">Live Polling Cadence</p>
              <p class="text-[11px] text-base-content/60">Background telemetry refresh frequency</p>
            </div>
          </div>
          <select 
            class="select select-bordered select-xs rounded-lg text-xs font-semibold"
            value={telemetryInterval}
            onchange={(e) => handleIntervalChange((e.target as HTMLSelectElement).value)}
          >
            <option value="5">Real-time (5s)</option>
            <option value="15">Standard (15s)</option>
            <option value="60">Eco (60s)</option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Row: Client Diagnostics & Cache Maintenance -->
  <div class="card bg-base-100 border border-base-content/10 shadow-xs rounded-2xl p-5 md:p-6">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h3 class="text-sm font-bold text-base-content flex items-center gap-2">
          <Database class="w-4 h-4 text-emerald-500" /> Client Cache & Build Information
        </h3>
        <p class="text-xs text-base-content/60 mt-0.5">
          Saya Intelligence Enterprise Client v2.4.0 • TimescaleDB IoT Engine • Session storage & active preferences
        </p>
      </div>

      <div class="flex items-center gap-2">
        {#if cacheCleared}
          <span class="text-xs text-emerald-500 font-bold flex items-center gap-1">
            <CheckCircle2 class="w-4 h-4" /> Cache Cleared!
          </span>
        {/if}
        <button 
          type="button" 
          onclick={clearClientCache}
          class="btn btn-outline btn-xs rounded-xl font-bold gap-1"
        >
          <RefreshCw class="w-3 h-3" /> Reset Client Cache
        </button>
      </div>
    </div>
  </div>
</div>
